import Vue from 'vue';
import { defineStore } from 'pinia';
import _ from 'lodash';
import uniqid from 'uniqid';

import languages from '@cospired/i18n-iso-languages';
languages.registerLocale(require('@cospired/i18n-iso-languages/langs/en.json'));
languages.registerLocale(require('@cospired/i18n-iso-languages/langs/de.json'));

import { getSettingValues } from '@/helpers';

import CmsApi from '@/store/api/cms/cms';
import SetupApi from '@/store/api/system/setup';
import IndicatorApi from '@/store/api/system/indicator';
import MemberTagApi from '@/extensions/restrictedArea/store/api/memberTag';
import WizardApi from '@/store/api/cms/wizard';

import { useSetupStore } from './setup';

const defaultForm = {
    id: '',
    nr: '',
    key: '',
    status: undefined,
    billing_text: undefined,
    billing_period: undefined,
    contract_beginning: undefined,
    contract_end: undefined,
    name: '',
    sub_title: '',
    i18n: false,
    is_blueprint: false,
    blueprint_name: '',
    blueprint_description: '',
    blueprint_url: '',
    has_business_card_per_stock: false,
    businessCard: {
        name: '',
        description: '',
        homepage_url: '',
        phone: '',
        email: '',
        category: null,
        opening_hours: {
            monday: [],
            tuesday: [],
            wednesday: [],
            thursday: [],
            friday: [],
            saturday: [],
            sunday: []
        },
        can_visit_location: false,
        has_delivery_service: false,
        image: null,
        // Address starts null — presence of `address` IS the override
        // signal. initFormBusinessCardAddress() seeds it with a
        // `new:xxx`-prefixed id when the admin opts in via the pencil
        // button. Defaulting to a populated object would render the editor
        // (and save an empty address row) on every fresh form.
        address: null,
    },
    businessCards: [],
    domains: [{ base_url: '' }],
    config: { template: '' },
    languages: [],
    images: [],
    blueprintScreenshots: [],
    extensions: [],
    settings: [],
    documentTemplatePartials: [],
    billingAddress: {
        gender: '',
        first_name: '',
        last_name: '',
        email: '',
        company: '',
        tax_id: '',
        street: '',
        zipcode: '',
        city: '',
        country: '',
        latitude: null,
        longitude: null
    }
};

const defaultRules = {
    name: 'required',
    domains: [{ base_url: 'required' }],
    shop: {},
    billingAddress: {
        street: 'required',
        zipcode: 'required',
        city: 'required',
        country: 'required'
    },
    businessCard: {
        description: 'html_max:300',
        category: 'required',
        homepage_url: 'url',
        email: 'email'
    },
    // Populated dynamically: addBusinessCardForStock pushes a rules slot per
    // card so prop="businessCards.<i>.description" resolves correctly.
    businessCards: []
};

// Rule template applied to every per-stock card. Keep in sync with the
// single-card businessCard rules above.
const perStockBusinessCardRules = () => ({
    description: 'html_max:300',
    category: 'required',
    homepage_url: 'url',
    email: 'email'
});

const businessCardAddressRules = () => ({
    street: 'required',
    zipcode: 'required',
    city: 'required',
    country: 'required'
});

const defaultUi = {
    form: {
        initialized: false,
        validateOnEnter: false
    }
};

export const useSettingsStore = defineStore('settings', {
    state: () => ({
        loading: true,
        activeCmsIndex: 0,
        cmssPaginated: {
            scope: 'base',
            type: 'cms',
            variables: {
                paginationSizes: [25, 50, 100]
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 25,
                selection: {
                    type: 'inclusive',
                    identifiers: [],
                    total: 0,
                },
            },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            defaultSort: null,
            link: {},
            items: []
        },
        options: {
            indicator: { loading: false, items: [] },
            template: { loading: false, items: [] },
            documentTemplatePartial: { loading: false, items: [] },
            language: {
                loading: false,
                items: Object.entries(languages.getNames('de'))
                    .map(([code, name]) => ({ code, name }))
            },
            cms: { loading: false, items: [] },
            status: {
                loading: false,
                items: [
                    { value: 'trial', label: 'Trial' },
                    { value: 'draft', label: 'Draft' },
                    { value: 'pro bono', label: 'Pro Bono' },
                    { value: 'billing', label: 'Billing' },
                    { value: 'terminated', label: 'Terminated' },
                ],
            },
            billingPeriod: {
                loading: false,
                items: [
                    { key: 'monthly', label: 'monthly', el: 'el-option' },
                    { key: 'quarterly', label: 'quarterly', el: 'el-option' },
                    {
                        key: 'annually',
                        label: 'annually',
                        el: 'el-option-group',
                        options: [
                            { key: 'annually-jan', label: 'January' },
                            { key: 'annually-feb', label: 'February' },
                            { key: 'annually-mar', label: 'March' },
                            { key: 'annually-apr', label: 'April' },
                            { key: 'annually-may', label: 'May' },
                            { key: 'annually-jun', label: 'June' },
                            { key: 'annually-jul', label: 'July' },
                            { key: 'annually-aug', label: 'August' },
                            { key: 'annually-sep', label: 'September' },
                            { key: 'annually-oct', label: 'October' },
                            { key: 'annually-nov', label: 'November' },
                            { key: 'annually-dec', label: 'December' },
                        ]
                    },
                ],
            },
            memberTag: { loading: false, items: [] },
            businessCardCategory: { loading: false, items: [] },
            businessCardTag: { loading: false, items: [] },
        },
        form: _.cloneDeep(defaultForm),
        ui: _.cloneDeep(defaultUi),
        // Hint for BusinessCardsPerStock: stock_ids whose accordion panel
        // should pop open on next render (set by Form.vue's submit handler
        // when validation surfaces errors inside per-stock cards). Cleared
        // after the panel reacts so subsequent edits aren't auto-opened.
        invalidBusinessCardStockIds: []
    }),

    getters: {
        optionsWithLocale: (state) => {
            const setupStore = useSetupStore();
            const options = _.cloneDeep(state.options);
            const localeValue = _.get(setupStore, 'locale');
            if (localeValue) {
                options.language.items = Object.entries(languages.getNames(localeValue))
                    .map(([code, name]) => ({ code, name }));
            }
            return options;
        },
        formInitialized: (state) => state.ui.form.initialized,
        formValidateOnEnter: (state) => state.ui.form.validateOnEnter,
        values: (state) => {
            const settingDefaults = _.get(state.form, 'config.template.settings') || [];
            const customSettings = _.get(state.form, 'settings') || [];
            return getSettingValues(settingDefaults, customSettings);
        },
        // Validation rules derived from the current form. Defining as a
        // getter (vs mutable state) is the load-bearing piece — Vue 2 array
        // reactivity doesn't track index-based reads (`rules.businessCards[i]`),
        // so pushing a new entry into a state-held rules array does NOT
        // re-trigger vee-element's `isRequired` computed in nested form-items.
        // A getter rebuilds the whole `rules` object whenever the form
        // changes, so el-form-items always see a fresh rules reference and
        // re-evaluate. Without this, accordion form-items don't show
        // asterisks and `validate()` doesn't pick up their required rules.
        rules: (state) => {
            const rules = _.cloneDeep(defaultRules);

            // Business card validation only applies when the billing feature
            // is enabled. `is_business_card` is computed server-side from the
            // billing feature (see store/api/cms/cms.js). Without this gate the
            // `businessCard.category: 'required'` rule blocks saving the CMS
            // from ANY tab (e.g. /settings/api) whenever the feature is off and
            // no category was ever picked — surfacing as "Formular ist ungültig!".
            if (!_.get(state.form, 'is_business_card')) {
                rules.businessCard = {};
                rules.businessCards = [];
                return rules;
            }

            const singleAddress = _.get(state.form, 'businessCard.address');
            if (singleAddress && !singleAddress.deleted) {
                if (!rules.businessCard) rules.businessCard = {};
                rules.businessCard.address = businessCardAddressRules();
            }

            const cards = _.get(state.form, 'businessCards', []) || [];
            rules.businessCards = cards.map((card) => {
                const cardRules = perStockBusinessCardRules();
                if (card && card.address && !card.address.deleted) {
                    cardRules.address = businessCardAddressRules();
                }
                return cardRules;
            });

            return rules;
        }
    },

    actions: {
        // Pagination mutations
        addPaginatedAccountsToState(data) {
            this.cmssPaginated.items = data.items;
            this.cmssPaginated.pageInfo = _.cloneDeep(data.pageInfo);
            this.cmssPaginated.allColumns = _.cloneDeep(data.config.allColumns);
            this.cmssPaginated.link = _.cloneDeep(data.config.link);
            this.cmssPaginated.showFilters = _.get(data.config, 'showFilters') || false;
            this.cmssPaginated.defaultSort = _.get(data.config, 'defaultSort') || null;
        },

        defineActiveColumns(activeColumnKeys) {
            let activeColumns = [];
            _.each(this.cmssPaginated.allColumns, column => {
                const allowed = _.find(activeColumnKeys, value => column.key === value);
                if (allowed) activeColumns.push(column);
            });
            if (activeColumns.length) {
                this.cmssPaginated.activeColumns = activeColumns;
            }
        },

        defineItemsPerPage(perPage) {
            this.cmssPaginated.pageInfo.perPage = perPage;
        },

        defineCurrentPage(currentPage) {
            this.cmssPaginated.pageInfo.currentPage = currentPage;
        },

        defineFilterValue({ key, value }) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && value.in.length === 0) {
                Vue.delete(this.cmssPaginated.filters, key);
            } else {
                Vue.set(this.cmssPaginated.filters, key, value);
            }
        },

        defineFilterVisibility(value) {
            this.cmssPaginated.showFilters = value;
        },

        defineSorting({ key, value }) {
            this.cmssPaginated.variables.order = key;
            this.cmssPaginated.variables.direction = value;
        },

        paginationBulkSelectionCheckAll({ value, indeterminate }) {
            var selection = this.cmssPaginated.pageInfo.selection;
            if (value === true) {
                Vue.set(selection, 'type', 'exclusive');
                Vue.set(selection, 'identifiers', []);
                Vue.set(selection, 'total', this.cmssPaginated.pageInfo.total);
            } else {
                Vue.set(selection, 'type', 'inclusive');
                Vue.set(selection, 'identifiers', []);
                Vue.set(selection, 'total', selection.identifiers.length);
            }
        },

        paginationBulkSelectionCheckVisible({ value, indeterminate }) {
            var items = _.get(this.cmssPaginated, 'items') || [];
            var ids = items.map(function (element) { return parseInt(element.id); });

            var selection = this.cmssPaginated.pageInfo.selection;
            if (value === true) {
                Vue.set(selection, 'type', 'inclusive');

                if (!selection.identifiers.length) {
                    Vue.set(selection, 'identifiers', ids);
                } else {
                    Vue.set(selection, 'identifiers', [].concat(selection.identifiers, ids));
                }
            } else {
                Vue.set(selection, 'type', 'inclusive');
                Vue.set(selection, 'identifiers', selection.identifiers.filter(function (element) { return ids.includes(element) === false; }));
            }

            Vue.set(selection, 'identifiers', Array.from(new Set(selection.identifiers)));
            Vue.set(selection, 'total', selection.identifiers.length);
        },

        paginationBulkSelectionCheckItem(item) {
            var selection = this.cmssPaginated.pageInfo.selection;
            var id = parseInt(item.id);

            var checked = false;
            switch (selection.type) {
                case 'inclusive':
                    checked = !!~selection.identifiers.map(function (element) { return parseInt(element); }).indexOf(id);
                    break;
                case 'exclusive':
                    checked = !~selection.identifiers.map(function (element) { return parseInt(element); }).indexOf(id);
                    break;
            }

            var push = true;
            var increment = true;
            switch (selection.type) {
                case 'inclusive':
                    push = increment = checked === false;
                    break;
                case 'exclusive':
                    push = checked === true;
                    increment = checked === false;
                    break;
            }

            if (push === true) {
                var index = selection.identifiers.length;
                Vue.set(selection.identifiers, index, id);
            } else {
                var removeIndex = selection.identifiers.indexOf(id);
                if (removeIndex >= 0) Vue.delete(selection.identifiers, removeIndex);
            }

            if (increment === true) Vue.set(selection, 'total', selection.total + 1);
            else Vue.set(selection, 'total', selection.total - 1);
        },

        paginationBulkSelectionReset() {
            Vue.set(this.cmssPaginated.pageInfo, 'selection', {
                type: 'inclusive',
                identifiers: [],
                total: 0,
            });
        },

        updateLoading(value) {
            this.loading = value;
        },

        // resetRules() is no longer needed — `rules` is a getter derived
        // from `form`, so resetting the form (or replacing it via insertCms)
        // automatically resets the rules.

        resetUI() {
            this.ui = _.cloneDeep(defaultUi);
        },

        setInvalidBusinessCardStockIds(stockIds) {
            this.invalidBusinessCardStockIds = (stockIds || []).map((id) => parseInt(id, 10));
        },

        clearInvalidBusinessCardStockIds() {
            this.invalidBusinessCardStockIds = [];
        },

        insertCms(cms) {
            this.form = cms;
        },

        // Options mutations
        updateIndicatorOptionsLoading(value) {
            this.options.indicator.loading = value;
        },

        insertIndicatorOptions(items) {
            this.options.indicator.items = items;
        },

        updateCmsOptionsLoading(value) {
            this.options.cms.loading = value;
        },

        insertCmsOptions(items) {
            this.options.cms.items = items;
        },

        updateTemplateOptionsLoading(value) {
            this.options.template.loading = value;
        },

        insertTemplateOptions(templates) {
            this.options.template.items = templates;
        },

        updateDocumentTemplatePartialPresetOptionsLoading(value) {
            this.options.documentTemplatePartial.loading = value;
        },

        insertDocumentTemplatePartialPresetOptions(presets) {
            this.options.documentTemplatePartial.items = presets;
        },

        updateMemberTagOptionsLoading(value) {
            this.options.memberTag.loading = value;
        },

        insertMemberTagOptions(tags) {
            this.options.memberTag.items = tags;
        },

        updateBusinessCardCategoryOptionsLoading(value) {
            this.options.businessCardCategory.loading = value;
        },

        insertBusinessCardCategoryOptions(categories) {
            this.options.businessCardCategory.items = categories;
        },

        updateBusinessCardTagOptionsLoading(value) {
            this.options.businessCardTag.loading = value;
        },

        insertBusinessCardTagOptions(tags) {
            this.options.businessCardTag.items = tags;
        },

        // Form UI mutations
        setFormInitialized(flag) {
            this.ui.form.initialized = flag;
        },

        setFormValidateOnEnter(flag) {
            this.ui.form.validateOnEnter = flag;
        },

        // Form field mutations
        setFormNr(value) { this.form.nr = value; },
        setFormKey(value) { this.form.key = value; },
        setFormStatus(value) { this.form.status = value; },
        setFormBillingText(value) { this.form.billing_text = value; },
        setFormBillingPeriod(value) { this.form.billing_period = value; },

        addFormBillingAddress() {
            this.form.billingAddress = { id: uniqid('new:'), type: 'billing', default_billing: true };
        },

        setFormBillingAddressField(field, value) {
            if (this.form.billingAddress) {
                Vue.set(this.form.billingAddress, field, value);
            }
        },

        addFormAddress() {
            if (this.form.address && this.form.address.deleted) {
                Vue.delete(this.form.address, 'deleted');
            } else {
                this.form.address = {
                    id: uniqid('new:'),
                    type: 'main',
                    street: '',
                    zipcode: '',
                    city: '',
                    country: 'DE',
                    latitude: null,
                    longitude: null
                };
            }
        },

        removeFormAddress() {
            if (this.form.address) {
                Vue.set(this.form.address, 'deleted', true);
            }
        },

        setFormAddressField(field, value) {
            if (this.form.address) {
                Vue.set(this.form.address, field, value);
            }
        },

        setFormName(value) { this.form.name = value; },
        setFormSubTitle(value) { this.form.sub_title = value; },
        setFormAssignMemberTags(value) { Vue.set(this.form, 'assign_member_tags', value); },
        setFormEmailTrackingEnabled(value) { Vue.set(this.form, 'email_tracking_enabled', value); },
        setFormAssignableMemberTags(value) { Vue.set(this.form, 'assignableMemberTags', value); },
        setFormIndicators(value) { Vue.set(this.form, 'indicators', value); },
        setFormI18n(value) { this.form.i18n = value; },
        setFormIsBlueprint(value) { this.form.is_blueprint = value; },
        setFormBlueprintName(value) { this.form.blueprint_name = value; },
        setFormBlueprintDescription(value) { this.form.blueprint_description = value; },
        setFormBlueprintUrl(value) { this.form.blueprint_url = value; },

        // Business card setters
        ensureBusinessCard() {
            if (!this.form.businessCard) {
                Vue.set(this.form, 'businessCard', _.cloneDeep(defaultForm.businessCard));
            }
        },
        setFormBusinessCardDescription(value) { this.ensureBusinessCard(); this.form.businessCard.description = value; },
        setFormBusinessCardHomepageUrl(value) { this.ensureBusinessCard(); this.form.businessCard.homepage_url = value; },
        setFormBusinessCardPhone(value) { this.ensureBusinessCard(); this.form.businessCard.phone = value; },
        setFormBusinessCardEmail(value) { this.ensureBusinessCard(); this.form.businessCard.email = value; },
        setFormBusinessCardCategory(value) {
            this.ensureBusinessCard();
            Vue.set(this.form.businessCard, 'category', value || null);
        },
        setFormBusinessCardOpeningHours(value) { this.ensureBusinessCard(); this.form.businessCard.opening_hours = value; },
        setFormCanVisitLocation(value) { this.ensureBusinessCard(); this.form.businessCard.can_visit_location = value; },
        setFormHasDeliveryService(value) { this.ensureBusinessCard(); this.form.businessCard.has_delivery_service = value; },
        setFormBusinessCardMetaTags(values) {
            this.ensureBusinessCard();
            var tagOptions = (this.options.businessCardTag && this.options.businessCardTag.items) || [];
            var metaTags = values.map(function (tagName, index) {
                var existingTag = _.find(tagOptions, { name: tagName });
                if (existingTag) {
                    return { id: existingTag.id, key: existingTag.key, name: existingTag.name };
                }
                return { id: 'new:' + index, name: tagName };
            });
            Vue.set(this.form.businessCard, 'meta_tags', metaTags);
        },

        initFormBusinessCardAddress() {
            this.ensureBusinessCard();
            if (!this.form.businessCard.address || this.form.businessCard.address.deleted) {
                Vue.set(this.form.businessCard, 'address', {
                    id: uniqid('new:'),
                    street: '',
                    zipcode: '',
                    city: '',
                    country: 'DE',
                    latitude: null,
                    longitude: null,
                    type: 'business-card'
                });
            }
            // Address rules auto-derive via the `rules` getter once the
            // address exists on the form.
        },

        setFormBusinessCardAddressField(field, value) {
            this.ensureBusinessCard();
            if (this.form.businessCard.address) {
                Vue.set(this.form.businessCard.address, field, value);
            }
        },

        // Chain-of-stores: per-stock business card setters.
        // Cards are keyed by stock.id for stable identity independent of
        // array index (so picker reorders don't desync form state).
        setHasBusinessCardPerStock(value) {
            this.form.has_business_card_per_stock = !!value;
        },

        findBusinessCardIndexByStockId(stockId) {
            if (!this.form.businessCards) return -1;
            return _.findIndex(this.form.businessCards, function (card) {
                return card.stock && parseInt(card.stock.id, 10) === parseInt(stockId, 10);
            });
        },

        addBusinessCardForStock(stock) {
            if (!this.form.businessCards) {
                Vue.set(this.form, 'businessCards', []);
            }
            if (this.findBusinessCardIndexByStockId(stock.id) >= 0) {
                return;
            }
            var card = _.cloneDeep(defaultForm.businessCard);
            // Address starts null — presence of `card.address` IS the override
            // signal, so the form doesn't need a parallel flag.
            card.address = null;
            card.stock = {
                id: parseInt(stock.id, 10),
                key: stock.key,
                name: stock.name,
                address: stock.address ? _.cloneDeep(stock.address) : null,
            };
            this.form.businessCards.push(card);
            // Rules auto-derive from form via the `rules` getter — adding a
            // card grows the rules array on the next read.
        },

        removeBusinessCardForStock(stockId) {
            var index = this.findBusinessCardIndexByStockId(stockId);
            if (index >= 0) {
                this.form.businessCards.splice(index, 1);
            }
        },

        setBusinessCardsField(stockId, field, value) {
            var index = this.findBusinessCardIndexByStockId(stockId);
            if (index < 0) return;
            Vue.set(this.form.businessCards[index], field, value);
        },

        initBusinessCardsAddress(stockId) {
            var index = this.findBusinessCardIndexByStockId(stockId);
            if (index < 0) return;
            Vue.set(this.form.businessCards[index], 'address', {
                id: uniqid('new:'),
                street: '',
                zipcode: '',
                city: '',
                country: 'DE',
                latitude: null,
                longitude: null,
                type: 'business-card',
            });
        },

        removeBusinessCardsAddress(stockId) {
            var index = this.findBusinessCardIndexByStockId(stockId);
            if (index < 0) return;
            var address = this.form.businessCards[index].address;
            if (address && address.id && String(address.id).indexOf('new:') !== 0) {
                // Persisted — mark for deletion on the next save (same pattern as
                // AbonementBusinessCard.vue's removeBusinessCardAddress).
                Vue.set(address, 'deleted', true);
            } else {
                Vue.set(this.form.businessCards[index], 'address', null);
            }
        },

        setBusinessCardsAddressField(stockId, field, value) {
            var index = this.findBusinessCardIndexByStockId(stockId);
            if (index < 0 || !this.form.businessCards[index].address) return;
            Vue.set(this.form.businessCards[index].address, field, value);
        },

        addFormBlueprintScreenshot({ image }) {
            if (!this.form.blueprintScreenshots) {
                this.form.blueprintScreenshots = [];
            }
            this.form.blueprintScreenshots.push({ ...image, type: 'blueprint-screenshot' });
        },

        removeFormBlueprintScreenshot({ index }) {
            if (this.form.blueprintScreenshots && this.form.blueprintScreenshots[index]) {
                Vue.set(this.form.blueprintScreenshots[index], 'deleted', true);
            }
        },

        reorderFormBlueprintScreenshots(reorderedScreenshots) {
            const currentScreenshots = this.form.blueprintScreenshots || [];
            reorderedScreenshots.forEach((item, index) => {
                const screenshotIndex = _.findIndex(currentScreenshots, { id: item.id });
                if (screenshotIndex >= 0) {
                    Vue.set(this.form.blueprintScreenshots[screenshotIndex], 'position', index);
                }
            });
        },

        setFormLanguages(value) {
            this.form.languages = value.map(language => {
                const copy = { ...language };
                delete copy.name;
                return copy;
            });
        },

        setFormTemplate(value) {
            if (!this.form.config) {
                this.form.config = {};
            }
            this.form.config.template = value;
        },

        setFormExtensions(value) {
            Vue.set(this.form, 'extensions', value || []);
        },

        createFormShop() {
            const shop = _.get(this.form, 'shop');
            const isDeleted = _.get(shop, 'deleted');
            if (isDeleted) {
                Vue.delete(this.form.shop, 'deleted');
            } else if (shop) {
                this.form.shop = shop;
            } else {
                this.form.shop = {
                    name: '',
                    defaultCustomerGroup: {},
                    defaultTax: {}
                };
            }
        },

        deleteFormShop() {
            Vue.set(this.form.shop, 'deleted', true);
        },

        addFormExtension({ key }) {
            const extensions = _.get(this, 'form.extensions') || [];
            extensions.push({ key });
            this.form.extensions = extensions;
        },

        removeFormExtension({ key }) {
            const extensions = _.get(this, 'form.extensions') || [];
            const index = _.findIndex(extensions, { key });
            if (index >= 0) {
                this.form.extensions.splice(index, 1);
            }
        },

        setFormCmsApiKey({ index, field, value }) {
            if (this.form.apiKeys && this.form.apiKeys[index]) {
                Vue.set(this.form.apiKeys[index], field, value);
            }
        },

        addFormCmsApiKey(key) {
            if (this.form.apiKeys === undefined) {
                Vue.set(this.form, 'apiKeys', []);
            }
            this.form.apiKeys.push({
                id: uniqid('new:'),
                key,
                value: '',
                public: '',
                private: ''
            });
        },

        // SMTP mutations
        setFormSmtpField(field, value) {
            if (!this.form.smtp) {
                Vue.set(this.form, 'smtp', { id: uniqid('new:') });
            }
            Vue.set(this.form.smtp, field, value);
        },

        setFormNewsletterSmtpField(field, value) {
            if (!this.form.newsletterSmtp) {
                Vue.set(this.form, 'newsletterSmtp', { id: uniqid('new:') });
            }
            Vue.set(this.form.newsletterSmtp, field, value);
        },

        // Domain mutations
        setFormDomain({ index, value }) {
            this.form.domains[index].base_url = value;
        },

        setFormDomainUnderConstructionSince({ index, value }) {
            this.form.domains[index].under_construction_since = value;
        },

        setFormDomainAllowAdminAccess({ index, value }) {
            this.form.domains[index].allow_admin_access = !!value;
        },

        setFormDomainUnderConstructionMessage({ index, value }) {
            this.form.domains[index].under_construction_message = value;
        },

        setFormDomainGuestCustomerGroup({ index, value }) {
            // value: { id } or null
            Vue.set(this.form.domains[index], 'guestCustomerGroup', value);
        },

        addFormDomain() {
            this.form.domains.push({ base_url: '' });
        },

        deleteFormDomain({ index }) {
            this.form.domains.splice(index, 1);
        },

        // Image mutations
        addFormImage({ image }) {
            if (!this.form.images) {
                Vue.set(this.form, 'images', []);
            }
            this.form.images.push(image);
        },

        markFormImageAsDeleted({ index }) {
            Vue.set(this.form.images[index], 'deleted', true);
        },

        unmarkFormImageAsDeleted({ index }) {
            Vue.set(this.form.images[index], 'deleted', false);
        },

        removeFormImage({ index }) {
            this.form.images.splice(index, 1);
        },

        markFormBusinessCardImageAsDeleted() {
            this.ensureBusinessCard();
            if (this.form.businessCard.image) {
                Vue.set(this.form.businessCard.image, 'deleted', true);
            }
        },

        unmarkFormBusinessCardImageAsDeleted() {
            this.ensureBusinessCard();
            if (this.form.businessCard.image) {
                Vue.set(this.form.businessCard.image, 'deleted', false);
            }
        },

        removeFormBusinessCardImage() {
            this.ensureBusinessCard();
            if (this.form.businessCard.image && this.form.businessCard.image.id) {
                Vue.set(this.form.businessCard.image, 'deleted', true);
            } else {
                Vue.set(this.form.businessCard, 'image', null);
            }
        },

        setFormBusinessCardImage(image) {
            this.ensureBusinessCard();
            Vue.set(this.form.businessCard, 'image', image);
        },

        setFormBusinessCardImageSettings(settings) {
            this.ensureBusinessCard();
            if (this.form.businessCard.image) {
                Vue.set(this.form.businessCard.image, 'settings', settings);
            }
        },

        setFormBusinessCardImageTitle(title) {
            this.ensureBusinessCard();
            if (this.form.businessCard.image) {
                Vue.set(this.form.businessCard.image, 'title', title);
            }
        },

        setFormBusinessCardImageAlt(alt) {
            this.ensureBusinessCard();
            if (this.form.businessCard.image) {
                Vue.set(this.form.businessCard.image, 'alt', alt);
            }
        },

        setFormImageVisibility({ index, scope, value }) {
            if (scope !== 'disabled' && value === true) {
                this.form.images.forEach((image, key) => {
                    if (key !== index) Vue.set(this.form.images[key], scope, false);
                });
            }
            Vue.set(this.form.images[index], scope, value);
        },

        // Multistore: numbering settings live on the CMS (per store)
        setFormCmsField(field, value) {
            Vue.set(this.form, field, value);
        },

        // The store's own document sender. A field left empty is stored empty on
        // purpose - that is how a store says "keep taking this one from the shop".
        setFormDocumentSenderField(field, value) {
            if (!this.form.documentSender) Vue.set(this.form, 'documentSender', {});

            if (field.indexOf('address.') === 0) {
                if (!this.form.documentSender.address) Vue.set(this.form.documentSender, 'address', { type: 'main' });
                Vue.set(this.form.documentSender.address, field.replace('address.', ''), value);

                return;
            }

            Vue.set(this.form.documentSender, field, value);
        },

        // Shop mutations
        setFormShopField(field, value) {
            if (this.form.shop) {
                Vue.set(this.form.shop, field, value);
            }
        },

        addFormShopAddress() {
            if (this.form.shop.address && this.form.shop.address.deleted) {
                Vue.delete(this.form.shop.address, 'deleted');
            } else {
                this.form.shop.address = {
                    type: 'main',
                    street: '',
                    zipcode: '',
                    city: '',
                    country: 'DE',
                    default_billing: true,
                    default_shipping: true,
                };
            }
        },

        removeFormShopAddress() {
            Vue.set(this.form.shop.address, 'deleted', true);
        },

        setFormShopAddressField(field, value) {
            if (this.form.shop && this.form.shop.address) {
                Vue.set(this.form.shop.address, field, value);
            }
        },

        // Document template partial mutations
        updateFormDocumentTemplatePartial({ key, name, description, template, customerGroup }) {
            // `template` is a NOT NULL column. An inherit-only field toggled to "custom"
            // with no content (or an editor that emits null) must not send null — an
            // empty override is just an empty string. Coerce here so the save can't 500.
            const safeTemplate = template == null ? '' : template;
            const i = this.form.documentTemplatePartials.findIndex(el => el.key === key);
            if (i >= 0) {
                this.form.documentTemplatePartials[i].name = name;
                this.form.documentTemplatePartials[i].description = description;
                this.form.documentTemplatePartials[i].template = safeTemplate;
                this.form.documentTemplatePartials[i].customerGroup = customerGroup;
            } else {
                this.form.documentTemplatePartials.push({
                    id: uniqid('new:'),
                    key,
                    name,
                    description,
                    template: safeTemplate,
                    customerGroup,
                });
            }
        },

        deleteFormDocumentTemplatePartial({ key }) {
            const i = this.form.documentTemplatePartials.findIndex(el => el.key === key);
            if (i >= 0) this.form.documentTemplatePartials.splice(i, 1);
        },

        // Settings mutations
        setFormSetting({ index, value, options }) {
            if (value !== undefined) this.form.settings[index].value = value;
            if (options !== undefined) this.form.settings[index].options = options;
        },

        addFormSetting(setting) {
            this.form.settings.push(setting);
        },

        // API Actions
        async fetchCmssPaginated() {
            const data = await CmsApi.fetchCmssPaginated(this.cmssPaginated);
            this.addPaginatedAccountsToState(data);
            const activeColumnKeys = _.map(data.config.activeColumnKeys, 'key');
            this.defineActiveColumns(activeColumnKeys);
        },

        async fetchCms(id) {
            const cms = await CmsApi.fetchCms(id);
            this.insertCms(_.cloneDeep(cms));
        },

        processPaginationBulkAction(payload) {
            return CmsApi.processPaginationBulkAction(payload.action, payload.payload, this.cmssPaginated);
        },

        fetchIndicatorOptions() {
            this.updateIndicatorOptionsLoading(true);
            return IndicatorApi.fetchList()
                .then(function(indicators) {
                    this.updateIndicatorOptionsLoading(false);
                    this.insertIndicatorOptions(indicators);
                }.bind(this))
                .catch(function(err) {
                    this.updateIndicatorOptionsLoading(false);
                    throw err;
                }.bind(this));
        },

        async fetchCmsOptions(ownerID) {
            this.updateCmsOptionsLoading(true);
            try {
                const options = await CmsApi.fetchList(ownerID);
                this.updateCmsOptionsLoading(false);
                this.insertCmsOptions(options);
            } catch (err) {
                this.updateCmsOptionsLoading(false);
                throw err;
            }
        },

        async fetchTemplateOptions() {
            this.updateTemplateOptionsLoading(true);

            let templates = [];
            const administrators = _.get(this, 'form.administrators', []);
            administrators.forEach(admin => {
                let template = _.get(admin, 'cms.config.template');
                if (template) templates.push(template);
            });
            templates = _.uniqBy(templates, 'id');
            this.insertTemplateOptions(templates);

            try {
                const fetchedTemplates = await SetupApi.fetchTemplates();
                this.updateTemplateOptionsLoading(false);
                this.insertTemplateOptions(fetchedTemplates);
            } catch (err) {
                this.updateTemplateOptionsLoading(false);
                throw err;
            }
        },

        async fetchDocumentTemplatePartialPresetOptions() {
            this.updateDocumentTemplatePartialPresetOptionsLoading(true);
            try {
                const presets = await SetupApi.fetchDocumentTemplatePartialPresets();
                this.updateDocumentTemplatePartialPresetOptionsLoading(false);
                this.insertDocumentTemplatePartialPresetOptions(presets);
            } catch (err) {
                this.updateDocumentTemplatePartialPresetOptionsLoading(false);
                throw err;
            }
        },

        async fetchMemberTagOptions(cmsID) {
            this.updateMemberTagOptionsLoading(true);
            try {
                const tags = await MemberTagApi.fetchList(cmsID);
                this.updateMemberTagOptionsLoading(false);
                this.insertMemberTagOptions(tags);
            } catch (err) {
                this.updateMemberTagOptionsLoading(false);
                throw err;
            }
        },

        async fetchBusinessCardCategoryOptions() {
            this.updateBusinessCardCategoryOptionsLoading(true);
            try {
                const categories = await WizardApi.fetchBusinessCardCategories();
                this.updateBusinessCardCategoryOptionsLoading(false);
                this.insertBusinessCardCategoryOptions(categories || []);
            } catch (err) {
                this.updateBusinessCardCategoryOptionsLoading(false);
                throw err;
            }
        },

        async fetchBusinessCardTagOptions() {
            this.updateBusinessCardTagOptionsLoading(true);
            try {
                var tags = await WizardApi.fetchBusinessCardTags();
                this.updateBusinessCardTagOptionsLoading(false);
                this.insertBusinessCardTagOptions(tags || []);
            } catch (err) {
                this.updateBusinessCardTagOptionsLoading(false);
                throw err;
            }
        },

        async create(cms) {
            const result = await CmsApi.create(cms);
            this.insertCms(_.cloneDeep(result));
            return result;
        },

        async update(cms) {
            const result = await CmsApi.update(cms);
            this.insertCms(_.cloneDeep(result));

            // The settings screen edits the store the chrome is rendering from, so the
            // save has to reach it too - otherwise a sender configured here leaves the
            // "cannot send emails yet" banner up until the next full page load.
            useSetupStore().refreshActiveCms(_.cloneDeep(result));

            return result;
        },

        async delete(id) {
            await CmsApi.delete(id);
            this.reset();
            this.resetUI();
        },

        reset() {
            this.insertCms(_.cloneDeep(defaultForm));
        }
    }
});
